/**
 * Featured Products Tabs — Stanlee India
 * Tab switching with a per-tab hero image/heading/description swap,
 * sliding underline indicator, smooth scroll, and scroll-reveal for
 * the product grid. No page reload.
 *
 * Unchanged from the previous version on purpose: the tab-switching
 * architecture already reads any tab via its data-tab attribute, so
 * adding the "Others" tab (Part 1/2) required zero changes here —
 * it is picked up automatically. This file also still listens for
 * the "stanlee:activate-tab" event, now broadcast by
 * assets/stanlee-navigation.js (the centralized nav controller —
 * see Part 5) whenever a mega-menu item, mobile accordion item, or
 * the Hero's "Explore Products" button is clicked.
 */
(function () {
  'use strict';

  var prefersReducedMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function initSection(sectionEl) {
    var tabs = Array.prototype.slice.call(sectionEl.querySelectorAll('.fpt-tab'));
    var underline = sectionEl.querySelector('.fpt-tab-underline');
    var tabsWrap = sectionEl.querySelector('.fpt-tabs');
    var heroCard = sectionEl.querySelector('.fpt-hero-card');
    var heroImageWrap = sectionEl.querySelector('.fpt-hero-image-wrap');
    var heroContent = sectionEl.querySelector('.fpt-hero-content');
    var heroTitle = sectionEl.querySelector('.fpt-hero-title');
    var heroDesc = sectionEl.querySelector('.fpt-hero-desc');
    var exploreBtn = sectionEl.querySelector('.fpt-explore-btn');
    var productSections = Array.prototype.slice.call(sectionEl.querySelectorAll('.fpt-products-section'));

    if (!tabs.length) return;

    var headerOffset = 24;

    /* ---------- Tabs + hero swap ---------- */

    function moveUnderlineTo(tab) {
      if (!underline || !tabsWrap) return;
      var wrapRect = tabsWrap.getBoundingClientRect();
      var tabRect = tab.getBoundingClientRect();
      underline.style.left = (tabRect.left - wrapRect.left + tabsWrap.scrollLeft) + 'px';
      underline.style.width = tabRect.width + 'px';
    }

    function setActiveProductSection(targetId) {
      productSections.forEach(function (section) {
        if (section.id === targetId) {
          section.classList.add('is-active');
          section.style.display = 'block';
          section.style.animation = 'none';
          void section.offsetWidth;
          section.style.animation = '';
          revealCardsIn(section);
        } else {
          section.classList.remove('is-active');
          section.style.display = 'none';
        }
      });
    }

    /**
     * Swap the hero image for the given tab. Always re-reads the tab's own
     * data-hero-image — never falls back to a previously cached element —
     * so every tab reliably shows its own uploaded image instead of
     * whichever image happened to load first.
     */
    function swapHeroImage(tab) {
      if (!heroImageWrap) return;
      var newImage = tab.getAttribute('data-hero-image');
      var newHeading = tab.getAttribute('data-hero-heading') || '';

      var currentImg = heroImageWrap.querySelector('.fpt-hero-image');
      var currentPlaceholder = heroImageWrap.querySelector('.fpt-hero-image-placeholder');

      if (!newImage) {
        // No image configured for this tab — show the placeholder graphic.
        if (currentImg) currentImg.classList.remove('is-active');
        if (currentPlaceholder) currentPlaceholder.style.opacity = '1';
        return;
      }

      if (currentPlaceholder) currentPlaceholder.style.opacity = '0';

      if (currentImg) {
        currentImg.classList.remove('is-active');
        window.setTimeout(function () {
          currentImg.setAttribute('src', newImage);
          currentImg.setAttribute('alt', newHeading);
          currentImg.classList.add('is-active');
        }, 60);
      } else {
        // First time we have a real image to show (started on a placeholder).
        var img = document.createElement('img');
        img.className = 'fpt-hero-image';
        img.setAttribute('src', newImage);
        img.setAttribute('alt', newHeading);
        img.setAttribute('loading', 'lazy');
        heroImageWrap.appendChild(img);
        window.requestAnimationFrame(function () {
          img.classList.add('is-active');
        });
      }
    }

    function activateTab(tab, options) {
      options = options || {};

      tabs.forEach(function (t) {
        t.classList.remove('active');
        t.setAttribute('aria-selected', 'false');
      });
      tab.classList.add('active');
      tab.setAttribute('aria-selected', 'true');

      moveUnderlineTo(tab);

      var newHeading = tab.getAttribute('data-hero-heading');
      var newDescription = tab.getAttribute('data-hero-description');
      var newTarget = tab.getAttribute('data-target');

      if (heroContent) heroContent.classList.add('is-fading');

      swapHeroImage(tab);

      window.setTimeout(function () {
        if (heroTitle) heroTitle.textContent = newHeading || '';
        if (heroDesc) heroDesc.textContent = newDescription || '';
        if (heroContent) heroContent.classList.remove('is-fading');
      }, 220);

      if (exploreBtn && newTarget) exploreBtn.setAttribute('data-target', newTarget);
      if (newTarget) setActiveProductSection(newTarget);

      if (options.scroll) {
        var targetEl = document.getElementById(newTarget);
        if (targetEl) {
          window.setTimeout(function () {
            var top = targetEl.getBoundingClientRect().top + window.pageYOffset - headerOffset;
            window.scrollTo({ top: top, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
          }, 40);
        }
      }
    }

    /**
     * Activate a tab by its category ID (the same value used in
     * data-tab, e.g. "android-tv", "fire-tv", "webos-tv", "apple-tv",
     * "others"). Used by the "stanlee:activate-tab" listener below so
     * the header mega menu / mobile accordion / hero button can switch
     * tabs without duplicating any of the tab-switching logic above.
     */
    function activateTabByCategory(category) {
      var match = tabs.filter(function (t) {
        return t.getAttribute('data-tab') === category;
      })[0];
      if (match && !match.classList.contains('active')) {
        activateTab(match, { scroll: false });
      }
    }

    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        if (tab.classList.contains('active')) return;
        activateTab(tab, { scroll: false });
      });
    });

    if (exploreBtn) {
      exploreBtn.addEventListener('click', function () {
        var targetId = exploreBtn.getAttribute('data-target');
        var targetEl = targetId ? document.getElementById(targetId) : null;
        if (targetEl) {
          var top = targetEl.getBoundingClientRect().top + window.pageYOffset - headerOffset;
          window.scrollTo({ top: top, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
        }
      });
    }

    // Remote tab activation — fired by assets/stanlee-navigation.js
    // whenever a [data-category-link] element anywhere on the page
    // (mega menu, mobile accordion, hero "Explore Products" button)
    // is clicked.
    document.addEventListener('stanlee:activate-tab', function (event) {
      var category = event.detail && event.detail.category;
      if (category) activateTabByCategory(category);
    });

    var activeTab = sectionEl.querySelector('.fpt-tab.active') || tabs[0];
    moveUnderlineTo(activeTab);

    window.addEventListener('resize', debounce(function () {
      var current = sectionEl.querySelector('.fpt-tab.active') || tabs[0];
      moveUnderlineTo(current);
    }, 150));

    /* ---------- Scroll-reveal for product cards ---------- */

    var revealedSections = new WeakSet();

    function revealCardsIn(section) {
      if (revealedSections.has(section)) {
        section.querySelectorAll('.fpt-product-card').forEach(function (card) {
          card.classList.add('is-visible');
        });
        return;
      }

      var cards = Array.prototype.slice.call(section.querySelectorAll('.fpt-product-card'));

      if (prefersReducedMotion || !('IntersectionObserver' in window)) {
        cards.forEach(function (card) {
          card.classList.add('is-visible');
        });
        revealedSections.add(section);
        return;
      }

      var observer = new IntersectionObserver(
        function (entries, obs) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              var index = cards.indexOf(entry.target);
              window.setTimeout(function () {
                entry.target.classList.add('is-visible');
              }, Math.max(index, 0) * 60);
              obs.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
      );

      cards.forEach(function (card) {
        observer.observe(card);
      });

      revealedSections.add(section);
    }

    // Reveal the initially active section on load
    var initialActive = sectionEl.querySelector('.fpt-products-section.is-active');
    if (initialActive) revealCardsIn(initialActive);

    /* ---------- Hero entrance (eyebrow/title/desc/button stagger) ---------- */

    if (heroCard) {
      if (prefersReducedMotion || !('IntersectionObserver' in window)) {
        heroCard.classList.add('is-revealed');
      } else {
        var heroObserver = new IntersectionObserver(
          function (entries, obs) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting) {
                heroCard.classList.add('is-revealed');
                obs.disconnect();
              }
            });
          },
          { threshold: 0.3 }
        );
        heroObserver.observe(heroCard);
      }
    }

    /* ---------- Image shimmer → loaded ---------- */

    sectionEl.querySelectorAll('.fpt-product-image').forEach(function (img) {
      var wrap = img.closest('.fpt-product-image-wrap');
      if (!wrap) return;
      if (img.complete && img.naturalWidth) {
        wrap.classList.add('is-loaded');
      } else {
        img.addEventListener('load', function () {
          wrap.classList.add('is-loaded');
        });
      }
    });
  }

  function debounce(fn, wait) {
    var timeout;
    return function () {
      var args = arguments;
      var context = this;
      window.clearTimeout(timeout);
      timeout = window.setTimeout(function () {
        fn.apply(context, args);
      }, wait);
    };
  }

  function initAll() {
    var sections = document.querySelectorAll('.fpt-section');
    sections.forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target.querySelector('.fpt-section');
    if (section) initSection(section);
  });
})();
